import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { isEd25519Supported, generateKeypair, storeKeypair, loadKeypair, exportPublicKeyHex } from '../../services/disclosureKeys'
import { registerDisclosureKeypair, getDisclosureKeypair } from '../../services/api'

interface Props {
  orgId: string
}

export default function KeypairSetupBanner({ orgId }: Props) {
  const [supported, setSupported] = useState<boolean | null>(null)
  // LOCAL: does THIS device have a private key in IndexedDB?
  const [localHasKey, setLocalHasKey] = useState<boolean | null>(null)
  // REMOTE: does the org have ANY registered public keys?
  const [remoteHasKey, setRemoteHasKey] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function init() {
      const sup = await isEd25519Supported()
      if (cancelled) return
      setSupported(sup)
      if (!sup) {
        setLocalHasKey(false)
        setRemoteHasKey(false)
        return
      }

      try {
        const kp = await loadKeypair(orgId)
        if (!cancelled) setLocalHasKey(kp != null)
      } catch {
        if (!cancelled) setLocalHasKey(false)
      }

      try {
        const status = await getDisclosureKeypair()
        if (!cancelled) setRemoteHasKey(Boolean(status.has_keypair))
      } catch {
        if (!cancelled) setRemoteHasKey(false)
      }
    }
    init()
    return () => { cancelled = true }
  }, [orgId])

  async function handleGenerate() {
    setLoading(true)
    setError(null)
    try {
      const keypair = await generateKeypair()
      const hex = await exportPublicKeyHex(keypair.publicKey)
      await registerDisclosureKeypair(hex)
      await storeKeypair(keypair, orgId)
      setLocalHasKey(true)
      setRemoteHasKey(true)
      setSuccess(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate signing key')
    } finally {
      setLoading(false)
    }
  }

  if (supported === null || localHasKey === null || remoteHasKey === null) return null

  if (!supported) {
    return (
      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          Your browser does not support Ed25519 signing keys. Please upgrade to Chrome 113+, Safari 17+, or Firefox 130+ to use commitment signing.
        </p>
      </div>
    )
  }

  if (success) {
    return (
      <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-sm text-green-800">Signing key registered.</p>
      </div>
    )
  }

  if (localHasKey) return null

  if (remoteHasKey) {
    return (
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-sm font-medium text-blue-900 mb-1">Generate signing key on this device</h3>
        <p className="text-sm text-blue-800 mb-3">
          Your organisation already has registered signing keys. Add a key for this device so you can sign commitments here.
        </p>
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Generating…' : 'Generate device key'}
        </button>
      </div>
    )
  }

  return (
    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <h3 className="text-sm font-medium text-blue-900 mb-1">Set up signing key</h3>
      <p className="text-sm text-blue-800 mb-3">
        Generate a signing key for progressive disclosure.
      </p>
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Generating…' : 'Generate signing key'}
        </button>
        <Link
          to={`/keypair-setup?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`}
          className="text-sm font-medium text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline"
        >
          Set up with guided steps →
        </Link>
      </div>
    </div>
  )
}